import { useCallback } from 'react';
import { ApolloClient } from '@apollo/client';
import { SaveDocument, SaveDocumentTemplate } from '../../../../../utils/api/document-interface.types';
import { useDocumentState } from '../../../../../contexts/DocumentContext';
import { documentStateFromResponse } from '../../../../../utils/document-wizard-utils/document-state-from-response';

type UseLeaseDocumentTitleSaveOptions = {
  id: string;
  onError: () => void;
  save: SaveDocument;
  apolloClient: ApolloClient<object>;
};

type UseLeaseDocumentTemplateTitleSaveOptions = {
  id: string;
  onError: () => void;
  save: SaveDocumentTemplate;
  apolloClient: ApolloClient<object>;
};

type SaveTitle = (title: string) => void;

export const useLeaseDocumentTitleSave = ({
  id,
  save,
  onError,
  apolloClient,
}: UseLeaseDocumentTitleSaveOptions): SaveTitle => {
  const { updateDocumentState } = useDocumentState();
  return useCallback(
    (title: string) => {
      // User cannot delete title
      if (!title) {
        return;
      }

      updateDocumentState({ isPending: true });

      save({ documentId: id, title })(apolloClient)
        .then((response) => {
          updateDocumentState(documentStateFromResponse(response));
        })
        .catch(onError)
        .finally(() => {
          updateDocumentState({ isPending: false });
        });
    },
    [apolloClient, updateDocumentState, id, onError, save]
  );
};

export const useLeaseDocumentTemplateTitleSave = ({
  id,
  save,
  onError,
  apolloClient,
}: UseLeaseDocumentTemplateTitleSaveOptions): SaveTitle => {
  const { updateDocumentState } = useDocumentState();
  return useCallback(
    (name: string) => {
      // User cannot delete title
      if (!name) {
        return;
      }

      updateDocumentState({ isPending: true });

      save({ documentTemplateId: id, name })(apolloClient)
        .then((response) => {
          updateDocumentState(documentStateFromResponse(response));
        })
        .catch(onError)
        .finally(() => {
          updateDocumentState({ isPending: false });
        });
    },
    [apolloClient, updateDocumentState, id, onError, save]
  );
};
